// Relecture champ par champ : les réponses IA restent du texte React, sans interprétation HTML.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Textarea } from '@artosera/ui';
import type { PropositionCatalogage, DecisionCatalogage } from '@artosera/shared';
import { useGesteCatalogage } from '../../api/catalogage.js';
import { useApercuMedia } from '../../api/medias.js';
import styles from './Catalogage.module.css';

export function texteValeur(valeur: unknown): string {
  if (valeur === null || valeur === undefined) return '—';
  if (Array.isArray(valeur)) return valeur.join(', ');
  if (typeof valeur === 'object')
    return Object.entries(valeur)
      .map(([k, v]) => `${k.toUpperCase()} : ${String(v ?? '')}`)
      .join('\n');
  return String(valeur);
}

function PhotoVerso({ galerieSlug, id }: { galerieSlug: string; id: string }) {
  const apercu = useApercuMedia(galerieSlug, id);
  const { t } = useTranslation('regie');
  return apercu.data ? (
    <img className={styles.photo} src={apercu.data} alt={t('catalogage.verso')} />
  ) : null;
}

export function PropositionVue({
  proposition: p,
  galerieSlug,
  versionFiche,
  actuelles,
  modifiable,
  provenance,
  recharger,
  onBiographieAcceptee,
}: {
  proposition: PropositionCatalogage;
  galerieSlug: string;
  versionFiche: string;
  actuelles: Record<string, unknown>;
  modifiable: boolean;
  provenance: boolean;
  recharger: () => void;
  onBiographieAcceptee?: (valeur: { fr: string; en: string }) => void;
}) {
  const { t } = useTranslation('regie');
  const geste = useGesteCatalogage(galerieSlug);
  const [edition, setEdition] = useState<string | null>(null);
  const [fr, setFr] = useState('');
  const [en, setEn] = useState('');
  const [lectureTexte, setLectureTexte] = useState('');
  const [libelle, setLibelle] = useState('');
  const [erreur, setErreur] = useState(false);
  function decider(
    champ: string,
    decision: DecisionCatalogage['decision'],
    valeur?: DecisionCatalogage['valeur'],
    destination?: DecisionCatalogage['destination'],
  ) {
    setErreur(false);
    geste.mutate(
      {
        chemin: `propositions/${p.id}/decisions/${champ}`,
        corps: {
          version: p.version,
          versionFiche,
          decision,
          ...(valeur === undefined ? {} : { valeur }),
          ...(destination ? { destination } : {}),
        },
      },
      {
        onSuccess: () => {
          setEdition(null);
          if (champ === 'biographie' && decision !== 'refuser') {
            const acceptee = valeur ?? p.champs.find((c) => c.champ === 'biographie')?.valeur;
            if (acceptee && !Array.isArray(acceptee) && 'fr' in acceptee)
              onBiographieAcceptee?.(acceptee);
          }
          recharger();
        },
        onError: () => {
          setErreur(true);
          recharger();
        },
      },
    );
  }
  const interdit = !modifiable || geste.isPending;
  return (
    <section className={styles.champ}>
      <h3>{t(`catalogage.${p.type}`)}</h3>
      {p.etat === 'echec' && (
        <p role="alert">
          {t('catalogage.echec')}{' '}
          {t(`catalogage.causes.${p.cause}`, { defaultValue: t('catalogage.indisponible') })}
        </p>
      )}
      {p.etat === 'en_cours' && <p role="status">{t('catalogage.enCours')}</p>}
      {erreur && <p role="alert">{t('catalogage.conflit')}</p>}
      {p.champs.map((c) => (
        <div key={c.champ} className={styles.champ}>
          <h4>{t(`catalogage.champs.${c.champ}`)}</h4>
          {c.incertain && <p role="note">{t('catalogage.incertain')}</p>}
          <div className={styles.comparaison}>
            <div>
              <strong>{t('catalogage.actuelle')}</strong>
              <p className={styles.texte}>{texteValeur(actuelles[c.champ])}</p>
            </div>
            <div>
              <strong>{t('catalogage.proposee')}</strong>
              <p className={styles.texte}>{texteValeur(c.valeur)}</p>
            </div>
          </div>
          {c.etat === 'proposee' ? (
            <>
              {edition === c.champ && (
                <div className={styles.editeur}>
                  <label>
                    {c.champ === 'motsCles' ? t('catalogage.motsClesAide') : 'FR'}
                    <Textarea
                      value={fr}
                      maxLength={
                        c.champ === 'cartel'
                          ? 400
                          : c.champ === 'description'
                            ? 1200
                            : c.champ === 'technique'
                              ? 500
                              : 2000
                      }
                      onChange={(e) => setFr(e.target.value)}
                    />
                  </label>
                  {c.champ !== 'motsCles' && (
                    <label>
                      EN
                      <Textarea
                        value={en}
                        maxLength={
                          c.champ === 'cartel'
                            ? 400
                            : c.champ === 'description'
                              ? 1200
                              : c.champ === 'technique'
                                ? 500
                                : 2000
                        }
                        onChange={(e) => setEn(e.target.value)}
                      />
                    </label>
                  )}
                </div>
              )}
              <div className={styles.gestes}>
                <Button
                  disabled={
                    interdit ||
                    (c.champ === 'technique' &&
                      !!actuelles.technique &&
                      texteValeur(actuelles.technique)
                        .replace(/FR : |EN : /g, '')
                        .trim() !== '')
                  }
                  onClick={() =>
                    decider(
                      c.champ,
                      edition === c.champ ? 'modifier' : 'accepter',
                      edition === c.champ
                        ? c.champ === 'motsCles'
                          ? fr
                              .split(',')
                              .map((v) => v.trim())
                              .filter(Boolean)
                          : { fr, en }
                        : undefined,
                    )
                  }
                >
                  {t('catalogage.accepter')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={interdit}
                  onClick={() => {
                    setEdition(c.champ);
                    setFr(Array.isArray(c.valeur) ? c.valeur.join(', ') : c.valeur.fr);
                    setEn(Array.isArray(c.valeur) ? '' : c.valeur.en);
                  }}
                >
                  {t('catalogage.modifier')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={interdit}
                  onClick={() => decider(c.champ, 'refuser')}
                >
                  {t('catalogage.refuser')}
                </Button>
              </div>
            </>
          ) : (
            <p>{t(`catalogage.decisions.${c.etat}`)}</p>
          )}
        </div>
      ))}
      {p.lectures.length > 0 && (
        <div className={styles.comparaison}>
          <div>
            {p.mediaIds.map((id) => (
              <PhotoVerso key={id} galerieSlug={galerieSlug} id={id} />
            ))}
          </div>
          <div>
            {p.lectures.map((l, i) => (
              <div key={i} className={styles.champ}>
                <h4>{t(`catalogage.natures.${l.nature}`)}</h4>
                <p>{t(`catalogage.lisibilites.${l.lisibilite}`)}</p>
                <p className={styles.texte}>{l.texteLu}</p>
                <p>{[l.institution, l.lieu, l.date, l.numero].filter(Boolean).join(' · ')}</p>
                {l.ligneProposee && (
                  <p>
                    {l.ligneProposee.libelle} {l.ligneProposee.periode}
                  </p>
                )}
                {l.etat === 'proposee' ? (
                  <>
                    {edition === String(i) && (
                      <div className={styles.editeur}>
                        <label>
                          {t('catalogage.texteLu')}
                          <Textarea
                            value={lectureTexte}
                            maxLength={500}
                            onChange={(e) => setLectureTexte(e.target.value)}
                          />
                        </label>
                        {l.ligneProposee && (
                          <label>
                            {t('catalogage.ligne')}
                            <Textarea
                              value={libelle}
                              maxLength={300}
                              onChange={(e) => setLibelle(e.target.value)}
                            />
                          </label>
                        )}
                      </div>
                    )}
                    <div className={styles.gestes}>
                      <Button
                        disabled={interdit}
                        onClick={() =>
                          decider(
                            String(i),
                            edition === String(i) ? 'modifier' : 'accepter',
                            edition === String(i)
                              ? {
                                  nature: l.nature,
                                  texteLu: lectureTexte,
                                  institution: l.institution,
                                  lieu: l.lieu,
                                  date: l.date,
                                  numero: l.numero,
                                  lisibilite: l.lisibilite,
                                  ligneProposee: l.ligneProposee
                                    ? { ...l.ligneProposee!, libelle }
                                    : null,
                                }
                              : undefined,
                            'notes',
                          )
                        }
                      >
                        {t('catalogage.notes')}
                      </Button>
                      {provenance && l.ligneProposee && (
                        <Button
                          disabled={interdit}
                          onClick={() =>
                            decider(
                              String(i),
                              edition === String(i) ? 'modifier' : 'accepter',
                              edition === String(i)
                                ? {
                                    nature: l.nature,
                                    texteLu: lectureTexte,
                                    institution: l.institution,
                                    lieu: l.lieu,
                                    date: l.date,
                                    numero: l.numero,
                                    lisibilite: l.lisibilite,
                                    ligneProposee: { ...l.ligneProposee!, libelle },
                                  }
                                : undefined,
                              'provenance',
                            )
                          }
                        >
                          {t('catalogage.provenance')}
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        disabled={interdit}
                        onClick={() => {
                          setEdition(String(i));
                          setLectureTexte(l.texteLu);
                          setLibelle(l.ligneProposee?.libelle ?? '');
                        }}
                      >
                        {t('catalogage.modifier')}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={interdit}
                        onClick={() => decider(String(i), 'refuser')}
                      >
                        {t('catalogage.refuser')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <p>{t(`catalogage.decisions.${l.etat}`)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
